import React, { useState, useEffect, useRef } from "react";
import { useOpenPack } from "../hooks/useOpenPack";
import { usePack } from "../../src/hooks/usePack";
import { useBank } from "../hooks/useBank";
import { useQuickSell } from "../hooks/useQuickSell";
import { useUser } from "../hooks/useUser";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/PackPage.css";
import "../css/Pack.css";
import "../css/Card.css";
import "../css/Inventory.css";
import "../index.css";
import Card from "./Card";
import CoinIcon from "./CoinIcon";
import Unknown from "../assets/Logo.png";
import SteamLoginButton from "./SteamLoginButton";

const PackPage = () => {
  const {
    packs,
    hasOpenedDailyPack,
    dailyPackID,
    dailyPackRemainingTime,
    isLoading,
    error,
  } = usePack();
  const { isGuest } = useUser();
  const [showDailyPack, setShowDailyPack] = useState(true);
  const { fetchCoins, subtractCoins } = useBank();
  const [selectedPack, setSelectedPack] = useState(null);
  const [openedCardsID, setOpenedCardsID] = useState([]);
  const cardRefs = useRef([]);
  const [lockOpening, setLockOpening] = useState(false);
  const [coins, setCoins] = useState(0);
  const [timeLeft, setTimeLeft] = useState("");
  const [dailyPackTimeLeft, setDailyPackTimeLeft] = useState("");
  const [isOpening, setIsOpening] = useState(true);
  const [isGodPack, setIsGodPack] = useState(false); // State to track if the pack is a God Pack
  const [allCardsOpened, setAllCardsOpened] = useState(false); // State to track if all cards are opened
  const [selectedCardsForSell, setSelectedCardsForSell] = useState([]); // State to track selected cards for selling
  const [totalQuickSellValue, setTotalQuickSellValue] = useState(0); // State to track total quick sell value
  const [isQuickSelling, setIsQuickSelling] = useState(false); // State to track quick sell loading
  const [showQuickSellConfirmation, setShowQuickSellConfirmation] =
    useState(false); // State to track confirmation
  const [showBackStates, setShowBackStates] = useState([]); // Track which cards show back
  const [isOpeningStates, setIsOpeningStates] = useState([]); // Track which cards are opening (animation)
  const [cardsVisible, setCardsVisible] = useState(false); // Track when cards should be visible
  const timers = useRef([]);
  const isQuickOpeningRef = useRef(false);

  // Helper function to wait for DOM element to be ready
  const waitForElement = (selector, maxWait = 2000) => {
    return new Promise((resolve) => {
      const checkElement = () => {
        const element = document.querySelector(selector);
        if (element) {
          resolve(element);
        } else if (maxWait > 0) {
          maxWait -= 50;
          setTimeout(checkElement, 50);
        } else {
          resolve(null);
        }
      };
      checkElement();
    });
  };

  const openSelectPackPopUp = (pack) => {
    setSelectedPack(pack);
  };

  const closeSelectPackPopUp = () => {
    setSelectedPack(null);
  };

  const openPackOpeningPopUp = (cards, isGodPack) => {
    isQuickOpeningRef.current = false;
    setIsOpening(false); // Start with cards in closed state
    setIsGodPack(isGodPack);
    setOpenedCardsID(cards);
    setAllCardsOpened(false); // Reset the state when opening a new pack
    // Initialize showBack states - all cards start showing their backs
    setShowBackStates(Array(cards.length).fill(true));
    setIsOpeningStates(Array(cards.length).fill(false));
    setCardsVisible(false); // Hide cards initially
  };

  const closePackOpeningPopUp = () => {
    isQuickOpeningRef.current = false;
    setOpenedCardsID([]);
    setLockOpening(false);
    setIsGodPack(false);
    setShowQuickSellConfirmation(false);
    setShowBackStates([]);
    setIsOpeningStates([]);
    setSelectedCardsForSell([]);
    setCardsVisible(false); // Reset visibility
    clearTimers();
    // Reset card refs to ensure clean state for next pack opening
    cardRefs.current = [];
  };

  const quickOpen = () => {
    isQuickOpeningRef.current = true;
    setLockOpening(false);
    setIsOpening(false);
    // Set all cards to show front (not back) for quick open
    setShowBackStates(Array(openedCardsID.length).fill(false));
    setIsOpeningStates(Array(openedCardsID.length).fill(false));
    setCardsVisible(true); // Make sure cards are visible for quick open
    clearTimers();
    setAllCardsOpened(true); // Set the state to true when quick open is used
  };

  const clearTimers = () => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current = [];
  };

  const handleCardClick = async (card) => {
    if (!allCardsOpened) return; // Only allow selection when all cards are opened

    setShowQuickSellConfirmation(false);

    if (selectedCardsForSell.includes(card)) {
      // Remove card from selection
      setSelectedCardsForSell(
        selectedCardsForSell.filter(
          (selectedCard) => selectedCard._id !== card._id,
        ),
      );
    } else {
      // Check card's fantasy team status before adding to selectedCardsForSell
      const status = await fetchCardFantasyTeamStatus(card._id);
      if (status.status === "ACTIVE") {
        return;
      }
      // Add card to selection
      setSelectedCardsForSell([...selectedCardsForSell, card]);
    }
  };

  const quickSellSelectedCards = async () => {
    if (isQuickSelling || isGroupQuickSelling) {
      return;
    }

    setIsQuickSelling(true);
    try {
      const result = await groupQuickSell(
        selectedCardsForSell.map((card) => card._id),
      );
      if (result) {
        // Update coins after successful quick sell
        fetchCoins().then((result) => {
          setCoins(result);
        });
        // Clear selected cards and close popup
        setSelectedCardsForSell([]);
        closePackOpeningPopUp();
      }
    } catch (error) {
    } finally {
      setIsQuickSelling(false);
    }
  };

  const quickSellAllCards = async () => {
    if (!showQuickSellConfirmation) {
      setShowQuickSellConfirmation(true);
      return;
    }

    if (isQuickSelling || isGroupQuickSelling) {
      return;
    }

    setIsQuickSelling(true);
    try {
      // Filter out cards in active fantasy teams
      const sellableCards = [];
      for (const card of openedCardsID) {
        const status = await fetchCardFantasyTeamStatus(card._id);
        if (status.status !== "ACTIVE") {
          sellableCards.push(card);
        }
      }

      if (sellableCards.length === 0) {
        setIsQuickSelling(false);
        return;
      }

      const result = await groupQuickSell(
        sellableCards.map((card) => card._id),
      );
      if (result) {
        // Update coins after successful quick sell
        fetchCoins().then((result) => {
          setCoins(result);
        });
        // Close popup
        closePackOpeningPopUp();
      }
    } catch (error) {
    } finally {
      setIsQuickSelling(false);
      setShowQuickSellConfirmation(false);
    }
  };

  const { openPack, openDailyPack, data, loading } = useOpenPack();
  const { groupQuickSell, fetchCardFantasyTeamStatus, isGroupQuickSelling } =
    useQuickSell();

  const handleOpenPack = async (tier) => {
    if (!lockOpening) {
      setLockOpening(true); // Lock the function to prevent multiple executions
      try {
        await openPack(selectedPack._id, tier.type);
        fetchCoins().then((result) => {
          setCoins(result);
        });
      } catch (error) {
      } finally {
        setLockOpening(false); // Ensure the lock is released after the operation
      }
    }
  };

  const handleOpenDailyPack = async (dailyPackID) => {
    if (lockOpening === false) {
      try {
        await openDailyPack(dailyPackID);
        setShowDailyPack(false);
      } catch (error) {
        setLockOpening(false);
      }
      setLockOpening(true);
    }
  };

  useEffect(() => {
    fetchCoins().then((result) => {
      setCoins(result);
    });
  });

  // Daily pack countdown timer
  useEffect(() => {
    if (hasOpenedDailyPack && dailyPackRemainingTime) {
      let remaining = dailyPackRemainingTime;

      const updateTimer = () => {
        if (remaining <= 0) {
          setDailyPackTimeLeft("Available");
          return;
        }

        setDailyPackTimeLeft(formatRemainingTime(remaining));
        remaining -= 1000; // Subtract 1 second
      };

      updateTimer(); // Initial update
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    } else if (!hasOpenedDailyPack) {
      setDailyPackTimeLeft("");
    }
  }, [hasOpenedDailyPack, dailyPackRemainingTime]);

  useEffect(() => {
    if (selectedPack && selectedPack.expireDate) {
      const targetDate = new Date(selectedPack.expireDate).getTime();

      const updateTimer = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeLeft("");
    }
    // eslint-disable-next-line
  }, [selectedPack]);

  useEffect(() => {
    if (data && data.cards && data.cards[0]) {
      closeSelectPackPopUp();
      openPackOpeningPopUp(data.cards, data.isGodPack);
    }
  }, [data]);

  useEffect(() => {
    if (openedCardsID.length > 0 && openedCardsID[0]) {
      isQuickOpeningRef.current = false;
      setIsOpening(true); // Set isOpening to true when starting the sequence
      const baseDelay = isGodPack ? 600 : 400;
      const cardInterval = isGodPack ? 7200 : 3200; // Double interval for God Packs
      const flipDelay = 150;

      // Wait a short time for all cards to render/mount, then wait for all elements and start opening sequence
      const startTimer = setTimeout(async () => {
        if (isQuickOpeningRef.current) return;

        // Wait for all card elements to be ready before making them visible
        const cardElementPromises = openedCardsID.map((cardData) =>
          waitForElement(`.card-${cardData._id}`),
        );

        // Wait for all card elements to be ready
        await Promise.all(cardElementPromises);

        if (isQuickOpeningRef.current) return;

        // Now make all cards visible since they're all ready
        setCardsVisible(true);

        // Open cards one by one with intervals
        openedCardsID.forEach((cardData, index) => {
          const cardOpenTimer = setTimeout(
            async () => {
              if (isQuickOpeningRef.current) return;

              const cardId = cardData._id;

              // Wait for the DOM element to be ready
              const cardElement = await waitForElement(`.card-${cardId}`);
              if (isQuickOpeningRef.current) return;

              // Set card as opening and flip to show front simultaneously
              setIsOpeningStates((prev) => {
                const updated = [...prev];
                updated[index] = true;
                return updated;
              });

              // After animation delay, call openCard
              const flipTimer = setTimeout(() => {
                if (isQuickOpeningRef.current) return;

                // Function to attempt opening card with retry
                const attemptOpenCard = (
                  cardIdx,
                  attempt = 1,
                  maxAttempts = 3,
                ) => {
                  if (isQuickOpeningRef.current) return;

                  if (
                    cardRefs.current[cardIdx] &&
                    typeof cardRefs.current[cardIdx].openCard === "function" &&
                    cardElement
                  ) {
                    setShowBackStates((prev) => {
                      const updated = [...prev];
                      updated[index] = false;
                      return updated;
                    });
                    const openCardTimer = setTimeout(() => {
                      if (isQuickOpeningRef.current) return;
                      cardRefs.current[cardIdx].openCard();
                    }, flipDelay * 2);
                    timers.current.push(openCardTimer);

                    // Set isOpening to false after the card opening animation completes
                    const openingStateTimer = setTimeout(() => {
                      if (isQuickOpeningRef.current) return;
                      setIsOpeningStates((prev) => {
                        const updated = [...prev];
                        updated[cardIdx] = false;
                        return updated;
                      });
                    }, cardInterval);
                    timers.current.push(openingStateTimer);
                  } else if (attempt < maxAttempts) {
                    const retryTimer = setTimeout(() => {
                      attemptOpenCard(cardIdx, attempt + 1, maxAttempts);
                    }, 50 * attempt);
                    timers.current.push(retryTimer);
                  } else {
                    // Fallback: try to directly add the active class
                    const fallbackCardElement =
                      document.querySelector(`.card-${cardId}`) || cardElement;
                    if (fallbackCardElement) {
                      fallbackCardElement.classList.add("active");
                      fallbackCardElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center",
                      });

                      // Set isOpening to false for fallback as well
                      const fallbackOpeningStateTimer = setTimeout(() => {
                        if (isQuickOpeningRef.current) return;
                        setIsOpeningStates((prev) => {
                          const updated = [...prev];
                          updated[cardIdx] = false;
                          return updated;
                        });
                      }, cardInterval);
                      timers.current.push(fallbackOpeningStateTimer);
                    }
                  }
                };

                attemptOpenCard(index);
              }, flipDelay);
              timers.current.push(flipTimer);

              // Unlock opening after the last card is opened and fully animated
              if (index === openedCardsID.length - 1) {
                const finalTimer = setTimeout(() => {
                  if (isQuickOpeningRef.current) return;
                  setLockOpening(false);
                  setAllCardsOpened(true);
                }, cardInterval + flipDelay);
                timers.current.push(finalTimer);
              }
            },
            baseDelay + index * cardInterval,
          );
          timers.current.push(cardOpenTimer);
        });
      }, 300); // Short delay to ensure all cards are rendered
      timers.current.push(startTimer);
    }
  }, [openedCardsID, isGodPack]);

  useEffect(() => {
    const packContent = document.querySelector(".OpenPack-content");
    if (packContent) {
      const handleScroll = (event) => {
        if (event.deltaY !== 0) {
          packContent.scrollLeft += event.deltaY;
          event.preventDefault();
        }
      };
      packContent.addEventListener("wheel", handleScroll);
      return () => {
        packContent.removeEventListener("wheel", handleScroll);
      };
    }
  }, [openPack]);

  useEffect(() => {
    justifyContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedCardsID, allCardsOpened]);

  useEffect(() => {
    // Calculate total quick sell value for selected cards
    if (selectedCardsForSell.length > 0) {
      const total = selectedCardsForSell.reduce((sum, card) => {
        return sum + card.tier * card.stats[0].firepower;
      }, 0);
      setTotalQuickSellValue(total);
    } else {
      setTotalQuickSellValue(0);
    }
  }, [selectedCardsForSell]);

  const justifyContent = () => {
    const packContainer = document.querySelector(".OpenPack-content");

    const checkCardContainer = () => {
      const cardContainer = document.getElementById("card");
      if (packContainer && cardContainer) {
        const totalCardsWidth =
          openedCardsID.length * cardContainer.clientWidth;
        const isOverflowing = totalCardsWidth > packContainer.clientWidth;

        if (isOverflowing) {
          packContainer.style.justifyContent = "flex-start";
        } else {
          packContainer.style.justifyContent = "center";
        }

        // Clear the interval once the cardContainer is found and processed
        clearInterval(intervalId);
      }
    };

    // Retry every second until the cardContainer is found
    const intervalId = setInterval(checkCardContainer, 1000);
  };

  const formatDate = (unixTimestamp) => {
    const date = new Date(unixTimestamp);
    return date.toLocaleDateString("da-DK", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const formatRemainingTime = (milliseconds) => {
    if (!milliseconds) return "";

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="main-container col-10 offset-1">
      <div className="title-container">
        <h1 className="title ">Packs</h1>
      </div>

      <div
        className="packPage-packs-container"
        style={{ overflowY: isGuest ? "hidden" : "auto" }}
      >
        {isLoading && <div className="loader"></div>}
        {error && (
          <div className="no-packs-container">
            <p>NO PACKS AVAILABLE AT THE MOMENT</p>
          </div>
        )}
        {isGuest && <SteamLoginButton overlay={true} />}
        {packs.length !== 0 && !isLoading && !error && (
          <div
            className={`col-3 pack-container ${
              hasOpenedDailyPack ? "disabled" : ""
            }`}
            onClick={() => {
              !hasOpenedDailyPack &&
                dailyPackID &&
                showDailyPack &&
                handleOpenDailyPack(dailyPackID);
            }}
          >
            <h1 className="title-pack">Daily Pack</h1>
            <div className="card-container" style={{ position: "relative" }}>
              <div className="bronze-pack active static" id="card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1">
                  <clipPath id="svgPath" clipPathUnits="objectBoundingBox">
                    <path
                      fill="#000"
                      d="M 0.993 0.126 C 0.966 0.126 0.943 0.121 0.924 0.114 C 0.894 0.101 0.874 0.083 0.872 0.061 C 0.872 0.058 0.871 0.054 0.871 0.052 C 0.871 0.049 0.866 0.049 0.863 0.047 C 0.826 0.036 0.787 0.027 0.749 0.021 C 0.67 0.007 0.584 0 0.5 0 C 0.415 0 0.332 0.007 0.251 0.021 C 0.21 0.027 0.174 0.037 0.137 0.047 C 0.136 0.049 0.131 0.049 0.131 0.052 C 0.131 0.054 0.128 0.058 0.128 0.061 C 0.126 0.083 0.106 0.101 0.076 0.114 C 0.055 0.121 0.031 0.126 0.01 0.126 C 0.002 0.127 0.001 0.127 0.001 0.131 L 0.001 0.818 C 0.001 0.825 0.001 0.835 0.002 0.842 C 0.006 0.855 0.015 0.866 0.032 0.876 C 0.045 0.884 0.064 0.888 0.08 0.891 C 0.128 0.901 0.174 0.908 0.221 0.918 C 0.284 0.931 0.345 0.946 0.405 0.963 C 0.44 0.972 0.472 0.985 0.5 0.999 L 0.5 1 L 0.5 0.999 C 0.525 0.983 0.559 0.973 0.59 0.963 C 0.654 0.946 0.717 0.931 0.777 0.918 L 0.917 0.891 C 0.936 0.888 0.948 0.884 0.966 0.876 C 0.987 0.866 0.994 0.855 0.998 0.842 C 0.999 0.835 0.999 0.825 0.999 0.818 L 0.999 0.542 L 0.999 0.143 L 0.999 0.131 C 0.999 0.127 0.998 0.127 0.993 0.126 Z"
                    />
                  </clipPath>
                  <image
                    href={`${process.env.PUBLIC_URL}/cardBackground.png`}
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    clipPath="url(#svgPath)"
                    preserveAspectRatio="none"
                  ></image>
                  <image
                    href={Unknown}
                    x="20%"
                    y="15%"
                    width="60%"
                    height="60%"
                  />
                </svg>
                <div className="pack-bottom">
                  <div className="name">Daily Pack</div>
                  <div className="stats">
                    <div>
                      <ul>
                        {/* Doesn't update from db */}
                        <li>
                          <span>Bronze</span>
                          <span className="">90%</span>
                        </li>
                        <li>
                          <span>Silver </span>
                          <span className="">8%</span>
                        </li>
                        <li>
                          <span>Gold</span>
                          <span className="">1.5%</span>
                        </li>
                        <li>
                          <span>Legendary</span>
                          <span className="">0.5%</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                {/* Full overlay when pack is not available */}
                {hasOpenedDailyPack && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      borderRadius: "0.5vw",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </div>
            </div>
            <h1 className="price-pack">
              {!hasOpenedDailyPack && dailyPackID && showDailyPack
                ? "Free"
                : dailyPackTimeLeft || "Available tomorrow"}
            </h1>
          </div>
        )}
        {packs.length > 0 &&
          !isLoading &&
          packs.map((pack, index) => (
            <div
              key={index}
              className="col-3 pack-container"
              onClick={() => openSelectPackPopUp(pack)}
            >
              <h1 className="title-pack">{pack.name}</h1>
              <div>
                <img
                  className="pack-image"
                  src={pack.image ? pack.image : Unknown}
                  alt={pack.name}
                />
              </div>
              <h1 className="price-pack">
                {" "}
                Offer ends: {formatDate(pack.expireDate)}{" "}
              </h1>
            </div>
          ))}
      </div>

      {selectedPack && (
        <div className="popup-overlay" onClick={closeSelectPackPopUp}>
          <div
            className="SelectPack-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="overlay-close" onClick={closeSelectPackPopUp}>
              X
            </button>
            <h1 className="title-pack">{selectedPack.name}</h1>
            <h1 className="price-pack"> Offer ends: {timeLeft} </h1>
            <div className="SelectPack-content">
              {selectedPack.tiers.map((tier, index) => (
                <div
                  key={index}
                  className="pack-tier-container"
                  onClick={() => handleOpenPack(tier)}
                >
                  <div className="card-container">
                    <div
                      className={`${
                        tier.type === 1
                          ? "bronze-pack"
                          : tier.type === 2
                            ? "silver-pack"
                            : "gold-pack"
                      } active static`}
                      id="card"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1">
                        <clipPath
                          id="svgPath"
                          clipPathUnits="objectBoundingBox"
                        >
                          <path
                            fill="#000"
                            d="M 0.993 0.126 C 0.966 0.126 0.943 0.121 0.924 0.114 C 0.894 0.101 0.874 0.083 0.872 0.061 C 0.872 0.058 0.871 0.054 0.871 0.052 C 0.871 0.049 0.866 0.049 0.863 0.047 C 0.826 0.036 0.787 0.027 0.749 0.021 C 0.67 0.007 0.584 0 0.5 0 C 0.415 0 0.332 0.007 0.251 0.021 C 0.21 0.027 0.174 0.037 0.137 0.047 C 0.136 0.049 0.131 0.049 0.131 0.052 C 0.131 0.054 0.128 0.058 0.128 0.061 C 0.126 0.083 0.106 0.101 0.076 0.114 C 0.055 0.121 0.031 0.126 0.01 0.126 C 0.002 0.127 0.001 0.127 0.001 0.131 L 0.001 0.818 C 0.001 0.825 0.001 0.835 0.002 0.842 C 0.006 0.855 0.015 0.866 0.032 0.876 C 0.045 0.884 0.064 0.888 0.08 0.891 C 0.128 0.901 0.174 0.908 0.221 0.918 C 0.284 0.931 0.345 0.946 0.405 0.963 C 0.44 0.972 0.472 0.985 0.5 0.999 L 0.5 1 L 0.5 0.999 C 0.525 0.983 0.559 0.973 0.59 0.963 C 0.654 0.946 0.717 0.931 0.777 0.918 L 0.917 0.891 C 0.936 0.888 0.948 0.884 0.966 0.876 C 0.987 0.866 0.994 0.855 0.998 0.842 C 0.999 0.835 0.999 0.825 0.999 0.818 L 0.999 0.542 L 0.999 0.143 L 0.999 0.131 C 0.999 0.127 0.998 0.127 0.993 0.126 Z"
                          />
                        </clipPath>
                        <image
                          src={`${process.env.PUBLIC_URL}/cardBackground.png`}
                          x="0"
                          y="0"
                          width="100%"
                          height="100%"
                          clipPath="url(#svgPath)"
                          preserveAspectRatio="none"
                        ></image>
                        <image
                          href={selectedPack.image}
                          x="20%"
                          y="15%"
                          width="60%"
                          height="60%"
                        />
                      </svg>
                      <div className="pack-bottom">
                        <div className="name">
                          {tier.type === 1
                            ? "Bronze"
                            : tier.type === 2
                              ? "Silver"
                              : tier.type === 4
                                ? "Event"
                                : "Gold"}{" "}
                          Pack
                        </div>
                        <div className="stats">
                          <div>
                            <ul>
                              <li>
                                <span>Bronze</span>
                                <span className="">{tier.odds.bronze}%</span>
                              </li>
                              <li>
                                <span>Silver </span>
                                <span className="">{tier.odds.silver}%</span>
                              </li>
                              <li>
                                <span>Gold</span>
                                <span className="">{tier.odds.gold}%</span>
                              </li>
                              <li>
                                <span>Legendary</span>
                                <span className="">{tier.odds.legendary}%</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div></div>
                  <h1
                    style={{
                      color:
                        coins < tier.price
                          ? "var(--lightred)"
                          : coins >= tier.price
                            ? "var(--lightgreen)"
                            : "inherit",
                    }}
                    className="price-pack"
                  >
                    {" "}
                    <CoinIcon width="0.7vw" height="0.7vw" />{" "}
                    {tier.price && tier.price}
                  </h1>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div className="popup-overlay">
          <div className="loader" />
        </div>
      )}
      {openedCardsID && openedCardsID.length > 0 && (
        <div className="popup-overlay">
          <div
            className={`${
              isGodPack ? "god-pack-overlay" : "OpenPack-container"
            }`}
          >
            {isGodPack && <div className="god-pack-particles"></div>}
            <div className="OpenPack-content col-10">
              {openedCardsID.map((card, index) => (
                <div
                  key={index}
                  className={`cardDisplay-container ${
                    allCardsOpened &&
                    selectedCardsForSell.some(
                      (selectedCard) => selectedCard._id === card._id,
                    )
                      ? "highlighted-card"
                      : ""
                  }`}
                  style={{
                    paddingBottom: 0,
                    marginBottom: 0,
                    cursor: allCardsOpened ? "pointer" : "default",
                    opacity: cardsVisible ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                  }}
                  onClick={() => allCardsOpened && handleCardClick(card)}
                >
                  <div className={`${allCardsOpened && "card-hoverable"}`}>
                    <Card
                      ref={(el) => (cardRefs.current[index] = el)}
                      cardID={card._id}
                      size={"small"}
                      showBack={showBackStates[index]}
                      isOpening={isOpeningStates[index]}
                      isSlowOpening={isGodPack}
                      key={`${card._id}-${openedCardsID.length}`} // Force re-render when pack changes
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="OpenPack-footer">
              {(!isGodPack || (isGodPack && allCardsOpened)) && (
                <button
                  className="default-button close-button"
                  onClick={closePackOpeningPopUp}
                >
                  Close
                </button>
              )}
              {!isGodPack && (
                <>
                  {!allCardsOpened ? (
                    <button
                      className="default-button close-button"
                      onClick={quickOpen}
                    >
                      Quick Open
                    </button>
                  ) : (
                    <div
                      className="quick-sell-section"
                      style={{ position: "relative" }}
                    >
                      {selectedCardsForSell.length > 0 && (
                        <div className="quick-sell-info">
                          <span>
                            Selected: {selectedCardsForSell.length} cards
                          </span>
                          <span>
                            Total value:{" "}
                            <CoinIcon width="0.7vw" height="0.7vw" />{" "}
                            {totalQuickSellValue}
                          </span>
                        </div>
                      )}
                      <button
                        className={`${
                          allCardsOpened ? "red-button" : "default-button"
                        } close-button`}
                        onClick={
                          selectedCardsForSell.length > 0
                            ? quickSellSelectedCards
                            : quickSellAllCards
                        }
                        disabled={isQuickSelling}
                      >
                        {isQuickSelling ? (
                          <div className="loader-small" />
                        ) : selectedCardsForSell.length > 0 ? (
                          `Quick Sell (${selectedCardsForSell.length})`
                        ) : showQuickSellConfirmation ? (
                          "You Sure?"
                        ) : (
                          "Quick Sell All"
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackPage;
